import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, UploadCloud, X, FileText, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { US_STATES } from "@/data/content";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { trackFormSubmit, trackFormStart } from "@/lib/analytics";

const ACCEPT = ".pdf,.csv,.xlsx,.xls,.doc,.docx,.txt,.jpg,.jpeg,.png";

export default function Assessment() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", company: "", state: "",
    num_facilities: "", target_deadline: "", phone: "", message: "",
    service_interest: "Tier II Reporting Support", company_website: "",
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);
  const fileRef = useRef(null);

  useSEO({
    title: "Free Tier II Assessment",
    description:
      "Get your free Tier II assessment. Tell us about your facility and we'll review your Tier II reporting requirements and follow up — no obligation.",
    path: "/free-tier-ii-assessment",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onFirstInteraction = () => {
    if (!started) { setStarted(true); trackFormStart("assessment"); }
  };

  const onFiles = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    setUploading(true);
    for (const file of selected) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds the 10MB limit.`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, { id: res.data.id, name: res.data.filename }]);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Failed to upload ${file.name}.`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads/assessment", { ...form, file_ids: files.map((f) => f.id) });
      trackFormSubmit("assessment");
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section className="bg-slate-50 min-h-[70vh] flex items-center py-20">
        <div className="mx-auto max-w-xl px-5 text-center" data-testid="assessment-success">
          <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600" aria-hidden="true" />
          <h1 className="mt-6 font-heading text-3xl font-extrabold text-navy-900">Thank You — Your Request Has Been Received</h1>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">
            We will review your information and contact you regarding your Tier II reporting requirements.
          </p>
          <Link to="/resources" className="mt-8 inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-bold text-white transition-transform hover:-translate-y-0.5" data-testid="assessment-explore-resources">
            Explore our resources
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="relative overflow-hidden bg-navy-900 noise-overlay">
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-14 lg:py-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Free Assessment</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            Get Your Free Tier II Assessment
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-slate-300">
            Tell us a little about your facility. Our team will review your Tier II reporting requirements and follow
            up — no obligation.
          </p>
        </div>
      </section>

      <section className="bg-slate-50 py-14 lg:py-16">
        <div className="mx-auto max-w-2xl px-5 lg:px-8">
          <form onSubmit={submit} onFocus={onFirstInteraction} className="rounded-xl border border-slate-200 bg-white p-6 lg:p-8 space-y-6" data-testid="assessment-form">
            <input type="text" value={form.company_website} onChange={set("company_website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="first">First Name *</Label>
                <Input id="first" required value={form.first_name} onChange={set("first_name")} className="mt-1.5" data-testid="assess-first-name" />
              </div>
              <div>
                <Label htmlFor="last">Last Name *</Label>
                <Input id="last" required value={form.last_name} onChange={set("last_name")} className="mt-1.5" data-testid="assess-last-name" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="email">Work Email *</Label>
                <Input id="email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5" data-testid="assess-email" />
              </div>
              <div>
                <Label htmlFor="company">Company *</Label>
                <Input id="company" required value={form.company} onChange={set("company")} className="mt-1.5" data-testid="assess-company" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label>State</Label>
                <Select value={form.state} onValueChange={setSel("state")}>
                  <SelectTrigger className="mt-1.5" data-testid="assess-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="numfac">Number of Facilities</Label>
                <Input id="numfac" value={form.num_facilities} onChange={set("num_facilities")} className="mt-1.5" placeholder="e.g. 1, or 5" data-testid="assess-num-facilities" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="deadline">Reporting Deadline</Label>
                <Input id="deadline" value={form.target_deadline} onChange={set("target_deadline")} className="mt-1.5" placeholder="e.g. March 1, or 'not sure'" data-testid="assess-deadline" />
              </div>
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input id="phone" value={form.phone} onChange={set("phone")} className="mt-1.5" data-testid="assess-phone" />
              </div>
            </div>

            <div>
              <Label htmlFor="msg">Message (optional)</Label>
              <Textarea id="msg" rows={3} value={form.message} onChange={set("message")} className="mt-1.5" placeholder="Anything else we should know about your reporting needs?" data-testid="assess-message" />
            </div>

            <div>
              <Label>Optional document upload</Label>
              <p className="mt-1 text-xs text-slate-500">Chemical inventory, SDS or a prior Tier II report. PDF, Excel, CSV, Word, TXT or images up to 10MB each.</p>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm font-semibold text-slate-600 hover:border-amber-400 hover:bg-amber-50 transition-colors"
                data-testid="assess-upload-trigger"
              >
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <UploadCloud className="h-5 w-5" aria-hidden="true" />}
                {uploading ? "Uploading..." : "Click to upload documents"}
              </button>
              <input ref={fileRef} type="file" multiple accept={ACCEPT} onChange={onFiles} className="hidden" data-testid="assess-file-input" />
              {files.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid="assess-file-list">
                  {files.map((f) => (
                    <li key={f.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                      <span className="flex items-center gap-2 truncate text-slate-700">
                        <FileText className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                        <span className="truncate">{f.name}</span>
                      </span>
                      <button type="button" onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-red-500" aria-label="Remove file" data-testid={`remove-file-${f.id}`}>
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
                <p className="text-xs leading-relaxed text-slate-700">
                  Wasserlab provides reporting and compliance support. Requirements vary by state and facility, and final
                  determinations and submissions remain the client's responsibility.
                </p>
              </div>
            </div>

            <p className="text-xs text-slate-500 text-center">Your information is kept confidential and used only to respond to your request.</p>
            <button
              type="submit"
              disabled={submitting || uploading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-amber-500 px-6 py-4 text-base font-bold text-navy-900 transition-transform hover:-translate-y-0.5 hover:bg-amber-400 disabled:opacity-60"
              data-testid="assess-submit"
            >
              {submitting && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
              Get My Free Assessment
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
